import React from 'react';
import Serializer, {
  type SerializeArguments,
  type DeserializeArguments,
} from './Serializer';
import { normalizeHref } from '../../plugins/LinkPlugin/utils';
import type {
  AhaReferenceElement,
  RecordId,
  UserReferenceElement,
} from '../../types/aha-editor';
import { type Descendant } from 'slate';
import { isHTMLElementNode } from '../helpers/serializerHelpers';
import {
  isAhaReferenceElement,
  isUserReferenceElement,
} from '../../types/guards';
import isUrl from 'is-url';

const AHA_REFERENCE_REGEX = /^(\w+-((N|R|I|S|SP|E|\d+)-)?\d+)$/;

const AHA_REFERENCE_CLASS_ICONS = {
  Competitor: 'fa-solid fa-trophy',
  Epic: 'aha-icon aha-icon-epic-solid',
  Feature: 'fa-solid fa-grid-2 fa-fw',
  'Ideas::Idea': 'fa-solid fa-lightbulb',
  Idea: 'fa-solid fa-lightbulb',
  Initiative: 'fa-solid fa-sitemap',
  Iteration: 'fa-solid fa-input-pipe',
  MasterFeature: 'aha-icon aha-icon-epic-solid',
  MasterRelease: 'fa-solid fa-calendar-lines',
  Page: 'fa-solid fa-file-lines',
  Persona: 'fa-solid fa-address-card',
  'Polls::Poll': 'fa-solid fa-chart-simple',
  Release: 'fa-solid fa-calendar-lines',
  Requirement: 'fa-solid fa-list',
  StrategicImperative: 'fa-solid fa-flag',
  Task: 'fa-solid fa-check',
  'Ideas::IdeaOrganization': 'fa-solid fa-building',
} as const;

const buildReferenceUrl = (referenceUrl: string, referenceClass: string) => {
  const url = new URL(referenceUrl);

  url.searchParams.set('reference-class', referenceClass);

  return url.toString();
};

class SerializerAhaReferences extends Serializer {
  static deserialize({
    el,
    next,
    markAttributes,
  }: DeserializeArguments):
    | AhaReferenceElement
    | UserReferenceElement
    | Descendant[]
    | undefined {
    if (!isHTMLElementNode(el)) {
      return;
    }

    const tagName = el.tagName.toLowerCase();
    const isLink = tagName === 'a';
    const linkedElementType = el.dataset?.linkedElementType;
    const isPortalUserReference =
      tagName === 'span' && linkedElementType === 'Ideas::PortalUser';

    if (!isLink && !isPortalUserReference) return;

    const href = el.getAttribute('href') || '';
    let referenceClass;
    try {
      if (isUrl(href)) {
        const referenceUrl = new URL(href);
        referenceClass = new URLSearchParams(referenceUrl.search).get(
          'reference-class'
        );
      }
    } catch (error) {
      // Error parsing the url. Skip it because it is not a reference
    }

    const objectType = linkedElementType || referenceClass;
    const textContent = el.textContent || '';

    if (!objectType) return;

    if (isPortalUserReference) {
      return {
        type: 'user-reference',
        reference_class: 'Ideas::PortalUser',
        reference_name: textContent.slice(1),
        reference_id: el.dataset.linkedElementId as RecordId,
        children: next(el.childNodes, markAttributes),
      };
    } else if (objectType === 'User') {
      return {
        type: 'user-reference',
        reference_class: objectType,
        reference_name: textContent.slice(1),
        reference_url: href,
        children: next(el.childNodes, markAttributes),
      };
    } else {
      let reference_name: AhaReferenceElement['reference_name'];
      let reference_num: AhaReferenceElement['reference_num'] = '';

      if (textContent.startsWith('#')) {
        // NOTE: will leave this here to support existing reference starting with `#`
        el.textContent = reference_num = reference_name = textContent.slice(1);
      } else {
        reference_name = textContent.trim();

        // Trim child nodes as well
        el.textContent = reference_name;

        // NOTE: new reference won't start with # anymore so this populates the reference only if it was a valid one but avoid setting the reference number for exsiting references such as Goals
        if (reference_name.match(AHA_REFERENCE_REGEX)) {
          reference_num = reference_name;
        }
      }

      const reference: AhaReferenceElement = {
        type: 'aha-reference',
        reference_class: objectType as AhaReferenceElement['reference_class'],
        reference_url: buildReferenceUrl(href, objectType),
        reference_name,
        reference_num,
        children: next(el.childNodes, markAttributes),
      };

      if (el.dataset.visibleFields) {
        reference.reference_visibleFields = el.dataset.visibleFields.split(',');
      }

      return reference;
    }
  }

  static serialize({ node, children }: SerializeArguments): React.ReactNode {
    if (!isAhaReferenceElement(node) && !isUserReferenceElement(node)) return;

    if (!node.reference_class) {
      return <>{children}</>;
    }

    if (
      isUserReferenceElement(node) &&
      node.reference_class === 'Ideas::PortalUser'
    ) {
      return (
        <span
          data-linked-element-type={node.reference_class}
          data-linked-element-id={node.reference_id}
        >
          @guest
        </span>
      );
    }

    const url =
      node.reference_class === 'User'
        ? node.reference_url
        : buildReferenceUrl(node.reference_url, node.reference_class);

    return (
      <a
        href={normalizeHref(url)}
        data-linked-element-type={node.reference_class}
        data-visible-fields={
          isAhaReferenceElement(node)
            ? node.reference_visibleFields?.join(',')
            : undefined
        }
      >
        {isAhaReferenceElement(node) && (
          <>
            <i
              className={`${
                AHA_REFERENCE_CLASS_ICONS[node.reference_class]
              } aha-reference-icon`}
            />{' '}
          </>
        )}
        {node.reference_class === 'User'
          ? `@${node.reference_name}`
          : node.reference_num || node.reference_id}
      </a>
    );
  }
}

export default SerializerAhaReferences;
